import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useMediaQueryMatchesOnDesktop } from '@yonderland/andes-react';
import { useAemConfig } from '@as-react/common-components';
import { MobileNavScrollObserver, Popover, PopoverPosition, PopoverType, Searchbox, SearchResultBlock, SearchResultsNothingFound, usePopoverHandlers, useLockBodyScrollOnTablet, } from '@as/cotopaxi';
import { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { I18N_NAMESPACES } from '../../constants/i18nConstants';
import { hasItems } from '../../util/ArrayUtil';
import EnumUtil from '../../util/EnumUtil';
import { appendSearchQueryToUrl, getKeywordsSuggestionsUrl } from '../../util/SearchUtil';
import SessionStorageUtil from '../../util/SessionStorageUtil';
import { MenuSearchResultBlockVariant, } from '@as-react/common-types';
import { DELAY } from '../../constants/GeneralConstants';
import { bannerIds, blockTitles, NOTHING_FOUND_SESSION_KEY } from '../../constants/SearchConstants';
import { MenuSearchBarcodeReader } from './MenuSearchBarcodeReader';
export const MenuSearch = ({ createAdobeAnalyticsSetSearchTermAction, menuSearchItemGrouping, suggestionsParentLink, setSearchValue, searchValue, }) => {
    const { t } = useTranslation(I18N_NAMESPACES.navigation);
    const isDesktop = useMediaQueryMatchesOnDesktop();
    const searchInputElementRef = useRef(null);
    const searchResultsNothingFoundTranslations = {
        noResults: t(`${I18N_NAMESPACES.navigation}:flyout.no.search.results`),
    };
    const { anchorEl, handlePopoverOpen, handlePopoverClose } = usePopoverHandlers();
    const isPopoverOpen = !!anchorEl;
    const [selectedGroupAndItem, setSelectedGroupAndItem] = useState();
    const [mobileVirtualKeyboardOn, setMobileVirtualKeyboardOn] = useState(true);
    const [savedSearchValue, setSavedSearchValue] = useState(searchValue);
    const [suggestedSearchValue, setSuggestedSearchValue] = useState('');
    const [isSearchMenuActive, setIsSearchMenuActive] = useState(false);
    const hasSearchResults = !!menuSearchItemGrouping && hasItems(Object.keys(menuSearchItemGrouping));
    const { root, isShopLanding } = useAemConfig();
    const listerPageUrl = `${root}lister.html`;
    const onInputChangeHandler = (searchValueInput) => {
        setSelectedGroupAndItem(undefined);
        if (!!searchValueInput && !!(searchInputElementRef === null || searchInputElementRef === void 0 ? void 0 : searchInputElementRef.current)) {
            handlePopoverOpen(null, searchInputElementRef.current, false, true);
        }
        else if (isPopoverOpen) {
            setMobileVirtualKeyboardOn(true);
            handlePopoverClose();
        }
    };
    const onButtonBackClickHandler = () => {
        if (isPopoverOpen) {
            handlePopoverClose();
        }
        setIsSearchMenuActive(false);
    };
    const inputElementSetFocus = () => { var _a; return (_a = searchInputElementRef === null || searchInputElementRef === void 0 ? void 0 : searchInputElementRef.current) === null || _a === void 0 ? void 0 : _a.focus(); };
    const onInputClearClickHandler = () => {
        setSearchValue('');
        setSavedSearchValue('');
        setSelectedGroupAndItem(undefined);
        setSuggestedSearchValue('');
    };
    const getMenuSearchResultBlockVariantItemByValue = (value) => {
        const currentSelectedKey = EnumUtil.getItemBasedOnValue(MenuSearchResultBlockVariant, value);
        return MenuSearchResultBlockVariant[currentSelectedKey];
    };
    const onButtonSearchClickHandler = () => {
        if (searchValue.length === 0) {
            inputElementSetFocus();
            setIsSearchMenuActive(true);
            return;
        }
        else {
            setIsSearchMenuActive(false);
        }
        if (selectedGroupAndItem) {
            const selectedMenuSearchItem = menuSearchItemGrouping[selectedGroupAndItem.type].items[selectedGroupAndItem.index];
            if (selectedGroupAndItem.type === MenuSearchResultBlockVariant.SUGGESTIONS) {
                createAdobeAnalyticsSetSearchTermAction(selectedMenuSearchItem.name.trim());
                window.location.href = selectedMenuSearchItem.url;
            }
            else {
                window.location.href = appendSearchQueryToUrl(selectedMenuSearchItem.url, searchValue);
            }
        }
        else {
            createAdobeAnalyticsSetSearchTermAction(searchValue);
            window.location.href = getKeywordsSuggestionsUrl(suggestionsParentLink, searchValue);
        }
    };
    const updateSuggestedSearchValue = (menuSearchItem) => {
        if (menuSearchItem) {
            const { name, subtitle } = menuSearchItem;
            const _subtitle = subtitle && subtitle.length > 0 ? subtitle.slice(0, 2).join(', ') : '';
            setSuggestedSearchValue(`${name} ${_subtitle}`.trim());
        }
        else {
            setSuggestedSearchValue('');
        }
    };
    const handleArrowDown = () => {
        var _a, _b;
        const currentSelectedTypeAsString = ((_a = selectedGroupAndItem === null || selectedGroupAndItem === void 0 ? void 0 : selectedGroupAndItem.type) === null || _a === void 0 ? void 0 : _a.toString()) || Object.keys(menuSearchItemGrouping)[0];
        const currentSelectedType = getMenuSearchResultBlockVariantItemByValue(currentSelectedTypeAsString);
        const currentSelectedIndex = (selectedGroupAndItem === null || selectedGroupAndItem === void 0 ? void 0 : selectedGroupAndItem.index) !== undefined ? selectedGroupAndItem.index : -1;
        // Check if last within group
        if (currentSelectedIndex + 1 === ((_b = menuSearchItemGrouping[currentSelectedType]) === null || _b === void 0 ? void 0 : _b.items.length)) {
            const availableTypes = Object.keys(menuSearchItemGrouping);
            // Check if not last group
            if (availableTypes.indexOf(currentSelectedType) < availableTypes.length - 1) {
                const newTypeIndex = availableTypes.indexOf(currentSelectedType) + 1;
                const newSelectedType = getMenuSearchResultBlockVariantItemByValue(availableTypes[newTypeIndex]);
                // Move to new group
                setSelectedGroupAndItem({
                    type: newSelectedType,
                    index: 0,
                });
                updateSuggestedSearchValue(menuSearchItemGrouping[newSelectedType].items[0]);
            }
            else {
                const firstSelectedType = getMenuSearchResultBlockVariantItemByValue(Object.keys(menuSearchItemGrouping)[0]);
                setSelectedGroupAndItem({ type: firstSelectedType, index: 0 });
                updateSuggestedSearchValue(menuSearchItemGrouping[firstSelectedType].items[0]);
            }
        }
        else {
            setSelectedGroupAndItem({
                type: currentSelectedType,
                index: currentSelectedIndex + 1,
            });
            updateSuggestedSearchValue(menuSearchItemGrouping[currentSelectedType].items[currentSelectedIndex + 1]);
        }
    };
    const handleArrowUp = () => {
        const availableTypes = Object.keys(menuSearchItemGrouping);
        if (selectedGroupAndItem) {
            const type = (selectedGroupAndItem === null || selectedGroupAndItem === void 0 ? void 0 : selectedGroupAndItem.type) ||
                MenuSearchResultBlockVariant[availableTypes[0]];
            const index = (selectedGroupAndItem === null || selectedGroupAndItem === void 0 ? void 0 : selectedGroupAndItem.index) || 0;
            // Check if first within group
            if (index === 0) {
                // Check if not first in group
                if (availableTypes.indexOf(type) > 0) {
                    const newTypeIndex = availableTypes.indexOf(type) - 1;
                    const newSelectedType = getMenuSearchResultBlockVariantItemByValue(availableTypes[newTypeIndex]);
                    // Move to new group
                    setSelectedGroupAndItem({
                        type: newSelectedType,
                        index: menuSearchItemGrouping[newSelectedType].items.length - 1,
                    });
                    updateSuggestedSearchValue(menuSearchItemGrouping[newSelectedType].items[menuSearchItemGrouping[newSelectedType].items.length - 1]);
                }
                else {
                    setSelectedGroupAndItem(undefined);
                    updateSuggestedSearchValue(undefined);
                }
            }
            else {
                setSelectedGroupAndItem({
                    type,
                    index: index - 1,
                });
                updateSuggestedSearchValue(menuSearchItemGrouping[type].items[index - 1]);
            }
        }
        else {
            // Move to the last group
            const newSelectedType = getMenuSearchResultBlockVariantItemByValue(availableTypes[availableTypes.length - 1]);
            const newSelectedIndex = menuSearchItemGrouping[newSelectedType].items.length - 1;
            setSelectedGroupAndItem({
                type: newSelectedType,
                index: newSelectedIndex,
            });
            updateSuggestedSearchValue(menuSearchItemGrouping[newSelectedType].items[newSelectedIndex]);
        }
    };
    const onInputKeyUpHandler = (e) => {
        const { key } = e;
        switch (key) {
            // ESCAPE
            case 'Esc': // IE/Edge specific value
            case 'Escape':
                !!e && e.preventDefault();
                onInputClearClickHandler();
                break;
            // ENTER
            case 'Enter':
                !!e && e.preventDefault();
                onButtonSearchClickHandler();
                break;
            // ARROW_DOWN
            case 'Down': // IE/Edge specific value
            case 'ArrowDown':
                !!e && e.preventDefault();
                handleArrowDown();
                break;
            // ARROW_UP
            case 'Up': // IE/Edge specific value
            case 'ArrowUp':
                !!e && e.preventDefault();
                handleArrowUp();
                break;
            default:
                break;
        }
    };
    const onFocusHandler = () => {
        setMobileVirtualKeyboardOn(true);
        if (!isSearchMenuActive)
            setIsSearchMenuActive(true);
        if (!!savedSearchValue && !!(searchInputElementRef === null || searchInputElementRef === void 0 ? void 0 : searchInputElementRef.current)) {
            setTimeout(() => {
                handlePopoverOpen(null, searchInputElementRef.current, false, true);
            }, DELAY.DELAY_300); // CSS animation delay
        }
        else if (isPopoverOpen) {
            handlePopoverClose();
        }
    };
    // On input blur, the popup should get full height because the keyboard is hidden.
    const onBlurHandler = () => setMobileVirtualKeyboardOn(false);
    const searchItemClickHandler = (value) => {
        setSearchValue(`${value} `);
        inputElementSetFocus();
    };
    const onSubmitCatcher = () => false;
    const onBarcodeScanHandler = (barcode) => {
        window.location.href = appendSearchQueryToUrl(`${listerPageUrl}?input=scanner`, barcode);
    };
    const nothingFoundClick = () => {
        SessionStorageUtil.setItem(NOTHING_FOUND_SESSION_KEY, '1');
    };
    useEffect(() => {
        if (savedSearchValue === '' && hasSearchResults) {
            inputElementSetFocus();
        }
    }, [savedSearchValue]);
    useEffect(() => setSavedSearchValue(searchValue), [searchValue]);
    useLockBodyScrollOnTablet(isSearchMenuActive);
    return (_jsxs("form", { method: "GET", action: listerPageUrl, onSubmit: onSubmitCatcher, suppressHydrationWarning: true, children: [_jsx(Searchbox, { searchValue: selectedGroupAndItem && suggestedSearchValue ? suggestedSearchValue : searchValue, setSearchValue: setSearchValue, inputElRef: searchInputElementRef, onButtonBackClick: onButtonBackClickHandler, onButtonSearchClick: onButtonSearchClickHandler, onInputBlur: onBlurHandler, onInputChange: onInputChangeHandler, onInputClearClick: onInputClearClickHandler, onInputFocus: onFocusHandler, onInputKeyUp: onInputKeyUpHandler, placeholder: t('flyout.search.placeholder') }), _jsx(Popover, { anchorEl: anchorEl, type: PopoverType.STICKY, onClose: handlePopoverClose, mobileVirtualKeyboardOn: mobileVirtualKeyboardOn, hideArrow: true, noAnimation: true, stickyWrapper: !isDesktop, position: PopoverPosition.BOTTOM, children: hasSearchResults ? (_jsx(_Fragment, { children: Object.values(menuSearchItemGrouping).map((menuSearchItemGroup) => (_jsx(SearchResultBlock, { analyticsAreaName: bannerIds[menuSearchItemGroup.type], highlightValue: searchValue, items: menuSearchItemGroup.items, onItemSuggestionFillClick: searchItemClickHandler, selectedItemIndex: !!selectedGroupAndItem && selectedGroupAndItem.type === menuSearchItemGroup.type
                            ? selectedGroupAndItem.index
                            : undefined, showSuggestionFill: menuSearchItemGroup.enableSuggestionFill && !isDesktop, showTextSubtle: menuSearchItemGroup.showTextSubtle, title: t(blockTitles[menuSearchItemGroup.type]) }, menuSearchItemGroup.type))) })) : (_jsx(SearchResultsNothingFound, { analyticsAreaName: "header-search-suggest-zero-suggest", searchValue: searchValue, url: appendSearchQueryToUrl(listerPageUrl, searchValue), onClick: nothingFoundClick, translations: searchResultsNothingFoundTranslations })) }), _jsx(MobileNavScrollObserver, { forceNavToBeShown: isSearchMenuActive }), isShopLanding && _jsx(MenuSearchBarcodeReader, { onScan: onBarcodeScanHandler, minLength: 9 })] }));
};
